import React, { useEffect, useState } from "react";
import { LayoutGrid, Shield, MessageSquare, Settings as SettingsIcon, LifeBuoy, Users } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { PLAN_LABEL } from "@/lib/format";
import { Label } from "@/components/ui/label";
import { Logo, ThemeToggle, Footer } from "@/components/sg/Brand";
import { PortalView } from "@/pages/ClientPortal";
import { HelpCenter } from "@/pages/FaqPage";
import Workspace from "@/pages/Workspace";
import Assistant from "@/pages/Assistant";
import Settings from "@/pages/Settings";
import Customers from "@/pages/Customers";
import { UpgradeDialog } from "@/components/sg/UpgradeDialog";
import { CheckoutDialog } from "@/components/sg/CheckoutDialog";

const OWNER_ITEM = { id: "customers", label: "Customers", icon: Users };

const HUD = [
  { id: "workspace", label: "Workspace", icon: LayoutGrid },
  { id: "portal", label: "Client Portal", icon: Shield },
  { id: "chat", label: "AI Assistant", icon: MessageSquare },
  { id: "help", label: "Help", icon: LifeBuoy },
  { id: "settings", label: "Settings", icon: SettingsIcon },
];

function HudDock({ view, setView, isOwner }) {
  const items = isOwner ? [...HUD.slice(0, 4), OWNER_ITEM, HUD[4]] : HUD;
  return (
    <div className="fixed bottom-4 md:bottom-6 left-1/2 -translate-x-1/2 z-50 max-w-[calc(100vw-1rem)]" data-testid="hud-dock">
      <div className="flex items-center gap-0.5 rounded-full border border-border bg-card/85 backdrop-blur-xl px-1.5 py-1.5 shadow-xl">
        {items.map((it) => {
          const active = view === it.id;
          return (
            <button key={it.id} data-testid={`hud-${it.id}-btn`} onClick={() => setView(it.id)} aria-label={it.label}
              className={`flex items-center gap-2 rounded-full px-3 md:px-4 py-2 text-sm font-medium transition-[background-color,color,transform] duration-200 hover:-translate-y-0.5 ${
                active ? "bg-indigo-600 text-white" : "text-muted-foreground hover:text-foreground hover:bg-accent"}`}>
              <it.icon className="h-4 w-4" /><span className="hidden xl:inline whitespace-nowrap">{it.label}</span>
            </button>
          );
        })}
        <div className="w-px h-6 bg-border mx-1" />
        <ThemeToggle />
      </div>
    </div>
  );
}

function PortalPreview() {
  const [projects, setProjects] = useState([]);
  const [token, setToken] = useState(null);
  useEffect(() => {
    api.get("/projects").then((r) => { setProjects(r.data); if (r.data[0]) setToken(r.data[0].client_token); });
  }, []);
  if (projects.length === 0) return <div className="max-w-md mx-auto p-10 text-center text-muted-foreground" data-testid="portal-preview-empty">Create a project first to preview what your client sees.</div>;
  return (
    <div>
      <div className="max-w-md mx-auto pt-8 px-5">
        <div className="text-xs text-muted-foreground mb-3">This is exactly what your client sees at their secure link. Approvals here are real.</div>
        <Label>Preview portal for</Label>
        <select data-testid="portal-preview-select" value={token || ""} onChange={(e) => setToken(e.target.value)}
          className="w-full mt-1 border border-input bg-background text-foreground rounded-md h-10 px-3 text-sm">
          {projects.map((p) => <option key={p.id} value={p.client_token}>{p.title || p.client_name}</option>)}
        </select>
      </div>
      {token && <PortalView key={token} token={token} preview />}
    </div>
  );
}

export default function AppShell() {
  const { user } = useAuth();
  const [view, setViewState] = useState(() => localStorage.getItem("sg_view") || "workspace");
  const setView = (v) => { setViewState(v); localStorage.setItem("sg_view", v); window.scrollTo({ top: 0 }); };
  const tab = (id, label, short) => (
    <button data-testid={`tab-${id}`} onClick={() => setView(id === "contractor" ? "workspace" : "portal")}
      className={`px-3 sm:px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${
        (id === "contractor" ? view === "workspace" : view === "portal") ? "bg-indigo-600 text-white" : "text-muted-foreground hover:text-foreground"}`}>
      <span className="hidden sm:inline">{label}</span><span className="sm:hidden">{short}</span>
    </button>
  );
  return (
    <div className="min-h-screen bg-background text-foreground pb-36">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between gap-3">
          <Logo to="/app" />
          <div className="flex items-center gap-1 border border-border rounded-full p-1 bg-card">
            {tab("contractor", "Contractor Workspace", "Workspace")}{tab("client", "Client Portal View", "Client View")}
          </div>
          <div className="hidden md:flex items-center gap-3 text-xs">
            <span className="px-2 py-1 rounded-full border border-border text-muted-foreground" data-testid="header-plan-badge">{user.role === "owner" ? "Owner" : PLAN_LABEL[user.plan || "free"]}</span>
            <span className="text-muted-foreground truncate max-w-[160px]" data-testid="header-user-name">{user.display_name || user.email}</span>
          </div>
        </div>
      </header>
      {view === "workspace" && <Workspace goTo={setView} />}
      {view === "portal" && <PortalPreview />}
      {view === "chat" && <Assistant goTo={setView} />}
      {view === "help" && <div className="max-w-7xl mx-auto px-4 md:px-8 py-8"><HelpCenter /></div>}
      {view === "settings" && <Settings />}
      {view === "customers" && user.role === "owner" && <Customers />}
      <Footer />
      <HudDock view={view} setView={setView} isOwner={user.role === "owner"} />
      <UpgradeDialog />
      <CheckoutDialog />
    </div>
  );
}
